// Centralized Axios Configuration with Interceptors
import axios from 'axios';
import { notification } from 'antd';
import config from '../config';
import { STORAGE_KEYS } from '../utils/constants';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: config.api.base_url,
  timeout: 30000,
});

// Request interceptor - adds auth token to all requests
api.interceptors.request.use(
  (requestConfig) => {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (token) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }
    return requestConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handles 401/403 errors globally
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const status = error.response?.status;

    if (status === 401) {
      // 401 Unauthorized - Session expired, redirect to login
      console.warn('Session expired - redirecting to login');
      localStorage.clear();
      window.location.replace('/login');
      return Promise.reject(error);
    }

    if (status === 403) {
      // 403 Forbidden - Skip notification for login-related errors (handled by Login component)
      const errorCode = error.response?.data?.data?.code;
      const isLoginError = ['ACCOUNT_PENDING', 'ACCOUNT_SUSPENDED'].includes(errorCode);

      if (!isLoginError) {
        notification.error({
          message: 'Access Denied',
          description: error.response?.data?.message || 'You do not have permission to perform this action.',
          placement: 'topRight',
          duration: 4,
        });
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Also set up the default axios instance for backward compatibility
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    if (status === 401) {
      console.warn('Session expired (default axios) - redirecting to login');
      localStorage.clear();
      window.location.replace('/login');
      return Promise.reject(error);
    }

    if (status === 403) {
      // Skip notification for login-related errors (handled by Login component)
      const errorCode = error.response?.data?.data?.code;
      const isLoginError = ['ACCOUNT_PENDING', 'ACCOUNT_SUSPENDED'].includes(errorCode);

      if (!isLoginError) {
        notification.error({
          message: 'Access Denied',
          description: error.response?.data?.message || 'You do not have permission to perform this action.',
          placement: 'topRight',
          duration: 4,
        });
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default api;
